/**
 * Answer choices.
 *
 * A choice may be text, a picture, or both, and there may be any number of them -
 * the teacher decides, not the software.
 *
 * Correctness is a radio when only one answer can be right and checkboxes when
 * several can. Checkboxes for a single-answer question invite ticking two, which
 * the question then rejects as an error the teacher has to go back and undo; a
 * radio makes the rule clear before it can be broken. "Not marked" stays available
 * because every answer key is optional.
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { Option, OptionIn } from '../../lib/api/client'
import { Button, IconButton, Input, Radio, Checkbox } from '../../components/ui'
import { Icon } from '../../components/icons'
import { subitemLabel } from '../../lib/labels'
import { PicturePicker } from './PicturePicker'

export function OptionsEditor({
  options,
  allowMultiple,
  disabled,
  onChange,
}: {
  options: Option[]
  allowMultiple: boolean
  disabled?: boolean
  onChange: (options: OptionIn[]) => void
}) {
  const [draft, setDraft] = useState<OptionIn[]>(toDraft(options))
  useEffect(() => setDraft(toDraft(options)), [options])

  const pictures = useQuery({
    queryKey: ['pictures', 'all'],
    queryFn: () => api.pictures({ per_page: 100 }),
  })
  const library = pictures.data?.images ?? []

  const commit = (next: OptionIn[]) => {
    setDraft(next)
    onChange(next)
  }

  const setCorrect = (index: number, correct: boolean) =>
    commit(
      draft.map((option, position) => ({
        ...option,
        // With one correct answer, choosing one clears the rest.
        is_correct: allowMultiple ? (position === index ? correct : option.is_correct) : position === index && correct,
      })),
    )

  return (
    <div className="tc-options">
      <div className="tc-options-head">
        <span className="tc-field-label">Choices</span>
        <span className="tc-field-hint">
          {allowMultiple
            ? 'Tick every choice that is correct. Marking answers is optional.'
            : 'Choose the correct answer, or leave it unmarked.'}
        </span>
      </div>

      {!allowMultiple && (
        <div className="tc-correct-row" role="radiogroup" aria-label="Which choice is correct?">
          <Radio
            name={`correct-${options[0]?.id ?? 'none'}`}
            label="Not marked"
            checked={!draft.some((option) => option.is_correct)}
            disabled={disabled}
            onChange={() => commit(draft.map((option) => ({ ...option, is_correct: false })))}
          />
          {draft.map((option, index) => (
            <Radio
              key={index}
              name={`correct-${options[0]?.id ?? 'none'}`}
              label={`(${letter(index)})`}
              checked={Boolean(option.is_correct)}
              disabled={disabled}
              onChange={() => setCorrect(index, true)}
            />
          ))}
        </div>
      )}

      {draft.map((option, index) => (
        <div key={options[index]?.id ?? index} className="tc-option-row">
          <span className="tc-option-letter">({letter(index)})</span>

          <Input
            value={option.text ?? ''}
            placeholder={`Choice ${index + 1}`}
            disabled={disabled}
            onChange={(event) =>
              setDraft(draft.map((o, i) => (i === index ? { ...o, text: event.target.value } : o)))
            }
            onBlur={() => onChange(draft)}
          />

          <PicturePicker
            library={library}
            chosen={option.image_id ?? null}
            disabled={disabled}
            onPick={(imageId) =>
              commit(draft.map((o, i) => (i === index ? { ...o, image_id: imageId } : o)))
            }
          />

          {allowMultiple && (
            <Checkbox
              label="Correct"
              checked={Boolean(option.is_correct)}
              disabled={disabled}
              onChange={(event) => setCorrect(index, event.target.checked)}
            />
          )}

          <IconButton
            label={`Remove choice ${letter(index)}`}
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={() => commit(draft.filter((_, i) => i !== index))}
          >
            <Icon.close size={14} />
          </IconButton>
        </div>
      ))}

      <Button
        size="sm"
        icon={<Icon.plus size={14} />}
        disabled={disabled}
        onClick={() => commit([...draft, { text: '', image_id: null, is_correct: false }])}
      >
        Add choice
      </Button>
    </div>
  )
}

function toDraft(options: Option[]): OptionIn[] {
  return options.map((option) => ({
    text: option.text ?? '',
    image_id: option.image_id ?? null,
    is_correct: option.is_correct,
    item_data: option.item_data ?? {},
  }))
}

/** Matches `option_label` in the renderer, so a choice is (b) everywhere. */
function letter(index: number) {
  return subitemLabel(index)
}
